import { getRealtimePrice, getRealtimePrices } from '@/api/market'

/**
 * 兼容 River 实时价格接口的响应格式。
 * 旧接口可能使用 data，新接口使用 result。
 */
function extractRealtimePayload(response) {
  if (!response) {
    return null
  }

  if (response.result !== undefined && response.result !== null) {
    return response.result
  }

  if (response.data !== undefined && response.data !== null) {
    return response.data
  }

  return null
}

/**
 * 从数字、字符串或价格对象中提取数值。
 */
function normalizeRealtimePrice(value) {
  if (value === undefined || value === null || value === '') {
    return null
  }

  if (typeof value === 'object') {
    const priceFields = [
      'price',
      'currentPrice',
      'nowPrice',
      'lastPrice',
      'closePrice',
      'close',
      'value'
    ]

    for (const field of priceFields) {
      if (
        value[field] !== undefined &&
        value[field] !== null &&
        value[field] !== ''
      ) {
        return normalizeRealtimePrice(value[field])
      }
    }

    return null
  }

  const numberValue = Number(value)

  return Number.isFinite(numberValue) ? numberValue : null
}

/**
 * 将批量价格响应统一转换为：
 * {
 *   bitcoin: 64020,
 *   AUDUSD: 0.65321
 * }
 */
function normalizeRealtimePriceMap(payload, requestedCodes) {
  const result = {}
  let source = payload

  if (
    source &&
    typeof source === 'object' &&
    !Array.isArray(source) &&
    source.prices &&
    typeof source.prices === 'object'
  ) {
    source = source.prices
  }

  if (Array.isArray(source)) {
    source.forEach(item => {
      if (!item || typeof item !== 'object') {
        return
      }

      const marketCode =
        item.marketCode ||
        item.code ||
        item.symbol

      const price = normalizeRealtimePrice(item)

      if (marketCode && price !== null) {
        result[marketCode] = price
      }
    })
  } else if (source && typeof source === 'object') {
    const sourceKeys = Object.keys(source)

    requestedCodes.forEach(marketCode => {
      let matchedKey = sourceKeys.find(
        key => key === marketCode
      )

      if (!matchedKey) {
        matchedKey = sourceKeys.find(
          key => key.toLowerCase() === marketCode.toLowerCase()
        )
      }

      if (matchedKey) {
        const price = normalizeRealtimePrice(source[matchedKey])

        if (price !== null) {
          result[marketCode] = price
        }
      }
    })

    // 单个产品接口偶尔直接返回一个价格对象。
    if (requestedCodes.length === 1 && result[requestedCodes[0]] === undefined) {
      const price = normalizeRealtimePrice(source)

      if (price !== null) {
        result[requestedCodes[0]] = price
      }
    }
  } else if (requestedCodes.length === 1) {
    const price = normalizeRealtimePrice(source)

    if (price !== null) {
      result[requestedCodes[0]] = price
    }
  }

  return result
}

/**
 * 价格轮询服务
 * 统一管理实时价格的HTTP轮询
 */
class PricePollingService {
  constructor() {
    this.pollingTimers = new Map() // 存储轮询定时器
    this.callbacks = new Map() // 存储回调函数
    this.batchTimer = null // 批量轮询定时器
    this.batchCallbacks = new Map() // 批量轮询回调
    this.batchMarketCodes = new Set() // 批量轮询的市场代码
  }

  /**
   * 开始轮询指定产品的价格
   * @param {string} marketCode 市场代码
   * @param {function} callback 价格更新回调函数
   * @param {number} interval 轮询间隔（毫秒），默认1秒
   */
  startPolling(marketCode, callback, interval = 1000) {
    console.log(`🎯 [价格轮询] 开始轮询 - marketCode: ${marketCode}, interval: ${interval}ms`)
    
    // 先停止已有的轮询
    this.stopPolling(marketCode)
    
    const poll = async () => {
      try {
        const res = await getRealtimePrice(marketCode)
        console.log(`🎯 [价格轮询] 接口响应 - ${marketCode}:`, res)
        const payload = extractRealtimePayload(res)
        const price = normalizeRealtimePrice(payload)

        if ((res.code === 200 || res.code === '200') && price !== null) {
          console.log(`🎯 [价格轮询] 价格更新 - ${marketCode}: ${price}`)
          callback(price)
        } else {
          console.warn(
            `🚨 [价格轮询] 获取价格失败 - ${marketCode}:`,
            res.msg || res.message || '无有效价格',
            res
          )
        }
      } catch (error) {
        console.error(`🚨 [价格轮询] 网络错误 - ${marketCode}:`, error)
      }
    }

    // 立即执行一次
    poll()
    
    // 设置定时轮询
    const timer = setInterval(poll, interval)
    this.pollingTimers.set(marketCode, timer)
    this.callbacks.set(marketCode, callback)
  }

  /**
   * 停止轮询指定产品
   * @param {string} marketCode 市场代码
   */
  stopPolling(marketCode) {
    const timer = this.pollingTimers.get(marketCode)
    if (timer) {
      console.log(`🎯 [价格轮询] 停止轮询 - marketCode: ${marketCode}`)
      clearInterval(timer)
      this.pollingTimers.delete(marketCode)
      this.callbacks.delete(marketCode)
    }
  }

  /**
   * 停止所有单独轮询
   */
  stopAllPolling() {
    this.pollingTimers.forEach(timer => clearInterval(timer))
    this.pollingTimers.clear()
    this.callbacks.clear()
  }

  /**
   * 添加到批量轮询
   * @param {string} marketCode 市场代码
   * @param {function} callback 价格更新回调函数
   * @param {number} interval 轮询间隔（毫秒），默认1秒
   */
  addToBatchPolling(marketCode, callback, interval = 1000) {
    
    this.batchMarketCodes.add(marketCode)
    this.batchCallbacks.set(marketCode, callback)
    
    // 如果还没有批量轮询，启动它
    if (!this.batchTimer && this.batchMarketCodes.size > 0) {
      this.startBatchPolling(interval)
    }
  }

  /**
   * 从批量轮询中移除
   * @param {string} marketCode 市场代码
   */
  removeFromBatchPolling(marketCode) {
    
    this.batchMarketCodes.delete(marketCode)
    this.batchCallbacks.delete(marketCode)
    
    // 如果没有产品了，停止批量轮询
    if (this.batchMarketCodes.size === 0) {
      this.stopBatchPolling()
    }
  }

  /**
   * 启动批量轮询
   * @param {number} interval 轮询间隔
   */
  startBatchPolling(interval = 1000) {
    
    const batchPoll = async () => {
      if (this.batchMarketCodes.size === 0) return
      
      try {
        const marketCodesArray = Array.from(this.batchMarketCodes)
        
        const res = await getRealtimePrices(marketCodesArray)
        const payload = extractRealtimePayload(res)
        const priceMap = normalizeRealtimePriceMap(
          payload,
          marketCodesArray
        )

        if (
          (res.code === 200 || res.code === '200') &&
          Object.keys(priceMap).length > 0
        ) {
          // 始终按原始请求代码触发回调，避免后端代码大小写不同。
          marketCodesArray.forEach(marketCode => {
            const callback = this.batchCallbacks.get(marketCode)
            const price = priceMap[marketCode]

            if (callback && price !== null && price !== undefined) {
              callback(price)
            }
          })
        } else {
          console.warn(
            `🚨 [批量轮询] 获取价格失败:`,
            res.msg || res.message || '无有效价格',
            res
          )
        }
      } catch (error) {
        console.error(`🚨 [批量轮询] 网络错误:`, error)
      }
    }

    // 立即执行一次
    batchPoll()
    
    // 设置定时轮询
    this.batchTimer = setInterval(batchPoll, interval)
  }

  /**
   * 停止批量轮询
   */
  stopBatchPolling() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer)
      this.batchTimer = null
    }
    this.batchMarketCodes.clear()
    this.batchCallbacks.clear()
  }

  /**
   * 清理所有轮询
   */
  cleanup() {
    console.log(`🎯 [价格轮询] 清理所有轮询`)
    this.stopAllPolling()
    this.stopBatchPolling()
  }

  /**
   * 获取当前轮询状态
   */
  getStatus() {
    return {
      singlePolling: this.pollingTimers.size,
      batchPolling: this.batchMarketCodes.size,
      hasBatchTimer: !!this.batchTimer
    }
  }
}

// 导出单例
export default new PricePollingService()
